/* global ActiveStorage */
import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['input', 'dropzone', 'previews', 'spinner']
  static values = { directUploading: Boolean }

  connect() {
    if (this.hasPreviewsTarget) {
      this.previewsTarget.classList.add('mt-4', 'grid', 'gap-4', 'grid-cols-2', 'sm:grid-cols-3')
    }
    this.activeUploads = 0
    this.uploadSucceeded = false
  }

  // Handles drag & drop events
  onDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
    this.dropzoneTarget.classList.add('ring-2', 'ring-[color:var(--theme-accent)]')
  }

  onDrop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove('ring-2', 'ring-[color:var(--theme-accent)]')
    this.disableSubmitButtons(true)
    const files = Array.from(event.dataTransfer.files)

    if (files.length === 0) {
      this.disableSubmitButtons(false)
      return
    }

    // Simulate selecting files via input
    const dataTransfer = new DataTransfer()
    files.forEach((file) => dataTransfer.items.add(file))
    this.inputTarget.files = dataTransfer.files

    // Trigger change
    this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }

  preview() {
    this.spinnerTarget.hidden = false
    this.previewsTarget.innerHTML = ''
    this.disableSubmitButtons(true)

    Array.from(this.inputTarget.files).forEach((file) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const wrapper = document.createElement('div')
        wrapper.className =
          'relative h-36 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm'
        const image = document.createElement('img')
        image.src = e.target.result
        image.className = 'h-full w-full object-cover'
        wrapper.appendChild(image)
        this.previewsTarget.appendChild(wrapper)
      }
      reader.readAsDataURL(file)
    })
  }

  validate() {
    const files = Array.from(this.inputTarget.files)
    const maxSize = 10 * 1024 * 1024

    for (const file of files) {
      if (!['image/png', 'image/jpeg', 'image/gif', 'application/pdf'].includes(file.type)) {
        this.showError(`File "${file.name}" is not an accepted format.`)
        this.inputTarget.value = ''
        this.disableSubmitButtons(false)
        return
      }

      if (typeof file.size === 'number' && file.size > maxSize) {
        this.showError(`File "${file.name}" is too large. Max is 10MB.`)
        this.inputTarget.value = ''
        this.disableSubmitButtons(false)
        return
      }
    }

    this.uploadFiles()
  }

  uploadFiles() {
    const files = Array.from(this.inputTarget.files)
    let url = this.inputTarget.dataset.directUploadUrl
    const lodgeSlug = this.inputTarget.dataset.lodgeSlug
    const category = this.inputTarget.dataset.category
    if (lodgeSlug || category) {
      const u = new URL(url, window.location.origin)
      if (lodgeSlug) u.searchParams.set('lodge_slug', lodgeSlug)
      if (category) u.searchParams.set('category', category)
      url = u.toString()
    }

    if (files.length === 0) return

    this.previewsTarget.innerHTML = ''
    this.directUploadStarted()
    this.activeUploads = files.length
    this.uploadSucceeded = false

    files.forEach((file) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const wrapper = document.createElement('div')
        wrapper.className =
          'relative h-36 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm'
        const image = document.createElement('img')
        image.src = e.target.result
        image.className = 'h-full w-full object-cover'
        wrapper.appendChild(image)
        this.previewsTarget.appendChild(wrapper)
      }
      reader.readAsDataURL(file)

      const upload = new ActiveStorage.DirectUpload(file, url)
      upload.create((error, blob) => {
        if (error) {
          console.error('❌ Direct upload failed', error)
          const shouldResetUi = this.activeUploads <= 1
          this.showError('Upload failed: ' + (error.message || error.toString()), shouldResetUi)
          this.finishSingleUpload()
          return
        }

        if (!blob) {
          console.error('❌ Direct upload failed with missing blob response', {
            fileName: file.name,
          })
          const shouldResetUi = this.activeUploads <= 1
          this.showError('Upload failed: no upload metadata returned.', shouldResetUi)
          this.finishSingleUpload()
          return
        }

        const hiddenField = document.createElement('input')
        hiddenField.type = 'hidden'
        hiddenField.name = this.inputTarget.name
        hiddenField.value = blob.signed_id

        this.inputTarget.closest('form').appendChild(hiddenField)

        this.uploadSucceeded = true
        this.finishSingleUpload()
      })
    })
  }

  directUploadStarted() {
    this.spinnerTarget.hidden = false
    this.disableSubmitButtons(true)
  }

  finishSingleUpload() {
    this.activeUploads -= 1
    if (this.activeUploads > 0) return

    this.directUploadEnd()
  }

  directUploadEnd() {
    this.spinnerTarget.hidden = true
    this.disableSubmitButtons(false)

    // Clear the file input so Rails' built-in direct-upload handler
    // does not try to re-upload the same files on the next submit.
    this.inputTarget.value = ''

    if (this.uploadSucceeded) {
      // Trigger autosave by dispatching a custom event
      this.element.dispatchEvent(new CustomEvent('receipt:uploaded', { bubbles: true }))
    }
  }

  disableSubmitButtons(disable) {
    document.querySelectorAll('input[type="submit"], button[type="submit"]').forEach((btn) => {
      btn.disabled = disable
      btn.classList.toggle('opacity-50', disable)
      btn.classList.toggle('cursor-not-allowed', disable)
    })
  }

  showError(message, resetUi = true) {
    if (resetUi) {
      this.spinnerTarget.hidden = true
      this.disableSubmitButtons(false)
    }
    this.previewsTarget.innerHTML = ''
    const error = document.createElement('div')
    error.className = 'text-red-500 text-sm'
    error.textContent = message
    this.previewsTarget.appendChild(error)
  }
}
